import { Typography } from '@mui/material';
import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { PartialInvoiceLogic } from '@work4all/models/lib/Classes/PartialInvoiceLogic.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../../tooltip/Tooltip';
import { ListEntityPicker } from '../components/list-entity-picker/ListEntityPicker';
import { IPickerProps } from '../types';

export type IPartialInvoiceCalculationLogicPickerProps<
  TMultiple extends boolean
> = IPickerProps<PartialInvoiceLogic, TMultiple>;

export function PartialInvoiceCalculationLogicPicker<TMultiple extends boolean>(
  props: IPartialInvoiceCalculationLogicPickerProps<TMultiple>
) {
  const { data, ...rest } = props;

  const allFields = useMemo(() => {
    return { ...PAYMENT_KIND_FIELDS, ...data };
  }, [data]);

  const request = useMemo<DataRequest>(() => {
    return {
      data: allFields,
      entity: Entities.partialInvoiceLogic,
      completeDataResponse: true,
      operationName: 'GetPartialInvoiceCalculationLogics',
    };
  }, [allFields]);

  const response = useDataProvider(request);

  // The data provider does not set the `total` property when
  // `completeDataResponse` is `true`.   So we have to set it manually.
  // Otherwise, the <ListEntityPicker /> will not be able to render the list.
  const patchedResponse = useMemo(() => {
    return { ...response, total: response.data.length };
  }, [response]);

  const renderItem = (partialInvoiceCalculationLogic: PartialInvoiceLogic) => {
    const label = partialInvoiceCalculationLogic.name;

    return (
      <Tooltip title={label}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <ListEntityPicker
      entity={Entities.user}
      data={allFields}
      filterBy={'note'}
      sortBy="note"
      {...rest}
      fixedDataSet={patchedResponse}
      renderItemContent={renderItem}
    />
  );
}

const PAYMENT_KIND_FIELDS: PartialInvoiceLogic = {
  id: null,
  name: null,
};
